import { Injectable } from '@angular/core';
import { DatePipe } from '@angular/common';
import { DateFormatConstants } from '../constants/date-formats';
import moment from 'moment';

@Injectable({
    providedIn: 'root'
})
export class DateUtilityService
{

    private datePipe = new DatePipe(DateFormatConstants.LOCALE);

    /**
     * Enhanced moment.js wrapper - provides consistent formatting with moment
     * @param date - Input date (any format)
     * @param format - Output format (defaults to MM/DD/YYYY)
     * @returns Formatted date string or null
     */
    formatWithMoment(date: any, format: string = 'MM/DD/YYYY'): string | null
    {
        if (!date) return null;

        try
        {
            const momentDate = moment(date);
            return momentDate.isValid() ? momentDate.format(format) : null;
        } catch (error)
        {
            console.warn('Date formatting error:', error, 'for value:', date);
            return null;
        }
    }

    /**
     * Consistent API date formatting using moment.js
     * @param date - Input date
     * @returns MM/DD/YYYY formatted string or null
     */
    formatForAPIAsString(date: any): string | null
    {
        return this.formatWithMoment(date, 'MM/DD/YYYY');
    }

    /**
     * Parse various date formats into a Date object (timezone-safe)
     * This is the main utility - parsing complex date inputs safely
     * @param date - Date object, string, or any date-like value
     * @returns Date object or null if invalid
     */
    parseDate(date: any): Date | null
    {
        if (!date) return null;

        // If already a Date object
        if (date instanceof Date)
        {
            return new Date(date.getTime()); // Clone to avoid mutation
        }

        // Handle form picker date objects
        if (date && date.date && typeof date.date === 'object')
        {
            const { year, month, day } = date.date;
            return new Date(year, month - 1, day); // month is 0-indexed
        }

        // Handle single date objects from date pickers
        if (date && date.singleDate && date.singleDate.jsDate)
        {
            return new Date(date.singleDate.jsDate);
        }

        // Handle formatted date strings
        if (date && date.singleDate && date.singleDate.formatted)
        {
            return new Date(date.singleDate.formatted);
        }

        // Handle string dates using moment.js for timezone-safe parsing
        if (typeof date === 'string')
        {
            const trimmed = date.trim();

            // Pure 'YYYY-MM-DD' (no time component) -> build a local-time Date
            // directly. moment(string) would parse this as UTC midnight, which
            // in negative-offset timezones becomes the previous calendar day.
            const isoDateOnly = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
            if (isoDateOnly)
            {
                const [, y, m, d] = isoDateOnly;
                return new Date(+y, +m - 1, +d);
            }

            // Common US format 'MM/DD/YYYY' -> build local-time Date directly.
            const usDateOnly = trimmed.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
            if (usDateOnly)
            {
                const [, m, d, y] = usDateOnly;
                return new Date(+y, +m - 1, +d);
            }

            // Full ISO timestamp pinned to UTC midnight (typical API "date only"
            // payload, e.g. "2024-03-15T00:00:00.000Z"). Take the calendar date
            // from the UTC components so the displayed day doesn't shift.
            if (/T00:00:00(?:\.0+)?Z$/i.test(trimmed))
            {
                const utc = moment.utc(trimmed);
                return utc.isValid()
                    ? new Date(utc.year(), utc.month(), utc.date())
                    : null;
            }

            const momentDate = moment(trimmed);
            return momentDate.isValid() ? momentDate.toDate() : null;
        }

        // Handle numeric timestamps
        if (typeof date === 'number')
        {
            return new Date(date);
        }

        return null;
    }

    /**
     * Format date for API submission as Date object (avoiding timezone issues)
     *
     * IMPORTANT: axios / JSON.stringify serializes Date via toISOString(), which
     * is always UTC. If we returned a Date at local midnight, in any non-UTC
     * timezone the serialized ISO string would land on the previous (or next)
     * calendar day on the server. Build the Date at UTC midnight so the wire
     * payload is e.g. "2024-03-15T00:00:00.000Z" regardless of the user's TZ.
     *
     * @param date - Input date
     * @returns Date object at UTC midnight, safe to JSON-serialize
     */
    formatForAPI(date: any): Date | null
    {
        if (!date) return null;

        try
        {
            const dateObj = this.parseDate(date);
            if (!dateObj || isNaN(dateObj.getTime())) return null;

            // Use UTC components so the resulting Date's ISO string preserves
            // the calendar date (no timezone shift on serialize).
            return new Date(Date.UTC(
                dateObj.getFullYear(),
                dateObj.getMonth(),
                dateObj.getDate()
            ));
        } catch (error)
        {
            console.warn('Date API formatting error:', error, 'for value:', date);
            return null;
        }
    }

    /**
     * Format date for HTML date input fields (YYYY-MM-DD)
     * @param date - Input date
     * @returns Date string in YYYY-MM-DD format for HTML date inputs
     */
    formatForInput(date: any): string | null
    {
        if (!date) return null;

        try
        {
            const dateObj = this.parseDate(date);
            if (!dateObj || isNaN(dateObj.getTime())) return null;

            return this.datePipe.transform(dateObj, DateFormatConstants.INPUT_FORMAT);
        } catch (error)
        {
            console.warn('Date input formatting error:', error, 'for value:', date);
            return null;
        }
    }

    /**
     * Format date picker values to YYYY-MM-DD format (replaces changeDateToApiFormulaTestDates)
     * Handles complex date picker objects and returns ISO date format
     * @param date - Date picker value or any date input
     * @returns YYYY-MM-DD formatted string or empty string
     */
    formatDatePickerToISO(date: any): string
    {
        if (!date) return '';

        // Handle date picker objects with nested date structure
        if (date && date.date && typeof date.date === 'object')
        {
            const { year, month, day } = date.date;
            return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        }

        // Handle single date objects
        if (date && date.singleDate && date.singleDate.formatted)
        {
            const momentDate = moment(date.singleDate.formatted);
            return momentDate.isValid() ? momentDate.format('MM-DD-YYYY') : '';
        }

        // Handle regular date objects and strings using moment for consistency
        const momentDate = moment(date);
        return momentDate.isValid() ? momentDate.format('MM-DD-YYYY') : '';
    }

    /**
     * Simple validation check
     * @param date - Date to validate
     * @returns True if date is valid
     */
    isValidDate(date: any): boolean
    {
        const dateObj = this.parseDate(date);
        return dateObj !== null && !isNaN(dateObj.getTime());
    }

    /**
     * Ensure the input is a proper Date object with fallback
     * This guarantees a valid Date object is returned
     * @param date - Input date (any format)
     * @param fallbackDate - Optional fallback date (defaults to current date)
     * @returns Valid Date object (never null)
     */
    ensureValidDate(date: any, fallbackDate?: Date): Date
    {
        try
        {
            const parsedDate = this.parseDate(date);

            // If parsing was successful and date is valid, return it
            if (parsedDate && !isNaN(parsedDate.getTime()))
            {
                return parsedDate;
            }

            // If no fallback provided, use current date
            const fallback = fallbackDate || new Date();

            console.warn('Invalid date provided:', date, 'Using fallback date:', fallback);
            return new Date(fallback); // Create new instance to avoid reference issues

        } catch (error)
        {
            console.warn('Date validation error:', error, 'for value:', date, 'Using current date as fallback');
            return fallbackDate ? new Date(fallbackDate) : new Date();
        }
    }

    /**
     * Convert date to MM/DD/YYYY string (for API calls that expect string format)
     * @param date - Input date
     * @returns MM/DD/YYYY string or null
     */
    toMMDDYYYYString(date: any): string | null
    {
        try
        {
            const dateObj = this.parseDate(date);
            if (!dateObj || isNaN(dateObj.getTime())) return null;

            return this.datePipe.transform(dateObj, DateFormatConstants.API_FORMAT);
        } catch (error)
        {
            console.warn('Date string formatting error:', error, 'for value:', date);
            return null;
        }
    }

    /**
     * Format date for timesheet calendar keys (MM-DD-YYYY format)
     * @param date - Input date
     * @returns MM-DD-YYYY formatted string or null
     */
    formatForTimesheetKey(date: any): string | null
    {
        return this.formatWithMoment(date, DateFormatConstants.TIMESHEET_KEY_FORMAT);
    }

    /**
     * Format date for display in UI using centralized format
     * @param date - Input date
     * @returns Formatted date string for display or null
     */
    formatForDisplay(date: any): string | null
    {
        if (!date) return null;

        try
        {
            const dateObj = this.parseDate(date);
            if (!dateObj || isNaN(dateObj.getTime())) return null;

            return this.datePipe.transform(dateObj, DateFormatConstants.DISPLAY_FORMAT);
        } catch (error)
        {
            console.warn('Date display formatting error:', error, 'for value:', date);
            return null;
        }
    }

    /**
     * Format date for tooltip display using centralized format
     * @param date - Input date
     * @returns Formatted date string for tooltips or null
     */
    formatForTooltip(date: any): string | null
    {
        if (!date) return null;

        try
        {
            const dateObj = this.parseDate(date);
            if (!dateObj || isNaN(dateObj.getTime())) return null;

            return this.datePipe.transform(dateObj, DateFormatConstants.TOOLTIP_FORMAT);
        } catch (error)
        {
            console.warn('Date tooltip formatting error:', error, 'for value:', date);
            return null;
        }
    }
}